'use client';

import { useMicrocopy } from '@contexts/MicrocopyContext';
import { buildPager } from '@coveo/atomic-react';
import { useController } from '@lib/hooks/useController';
import { MicrocopyItem } from '@lib/types/cms/MicrocopySet';
import { useCallback, useMemo } from 'react';
import { Pagination } from 'ui-components';
import { useEngine } from '../EngineContext';
import { PagerProps } from './Pager.types';

const getMicrocopyValue = (
  microcopySet: MicrocopyItem[] | undefined,
  key: string,
  fallback: string
) => {
  return microcopySet?.find((m) => m.key === key)?.value ?? fallback;
};

export const Pager = ({ className }: PagerProps) => {
  const { paginationMicrocopy: paginationMicrocopySet } = useMicrocopy<{
    paginationMicrocopy: MicrocopyItem[];
  }>();

  const eng = useEngine();
  const controller = useMemo(() => buildPager(eng), [eng]);
  const state = useController(controller);

  const handlePageChange = useCallback(
    (page: number) => {
      window.scrollTo({ top: 0 });
      controller.selectPage(page);
    },
    [controller]
  );

  const microcopy = useMemo(
    () => ({
      paginationAriaLabel: getMicrocopyValue(
        paginationMicrocopySet,
        'pagination.pagination-aria-label',
        'Pagination'
      ),
      previousAriaLabel: getMicrocopyValue(
        paginationMicrocopySet,
        'pagination.previous-aria-label',
        'Previous'
      ),
      nextAriaLabel: getMicrocopyValue(
        paginationMicrocopySet,
        'pagination.next-aria-label',
        'Next'
      ),
      pageAriaLabel: getMicrocopyValue(
        paginationMicrocopySet,
        'pagination.page-aria-label',
        'Page'
      ),
      firstAriaLabel: getMicrocopyValue(
        paginationMicrocopySet,
        'pagination.first-aria-label',
        'First'
      ),
      lastAriaLabel: getMicrocopyValue(
        paginationMicrocopySet,
        'pagination.last-aria-label',
        'Last'
      ),
    }),
    [paginationMicrocopySet]
  );

  return (
    <Pagination
      currentPage={state.currentPage}
      totalPages={state.maxPage}
      onPageChange={handlePageChange}
      className={className}
      microcopy={microcopy}
    />
  );
};
